import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { Skeleton } from 'antd'
import { useGeckoPermissions } from 'features/permissions'
import React, { ReactNode } from 'react'
import styled from 'styled-components'
import {
  GeckoOrganization,
  GeckoSite,
  PERMISSION_CANTILEVER_CORE_ADMIN,
} from 'types'

const StyledContainer = styled.div`
  padding: 0.5rem 0.75rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: none;
  border-radius: 0.375rem;
  background-color: ${({ theme }) => theme.forecast.background2};
  min-height: 32px;
  width: 100%;
  color: ${({ theme }) => theme.forecast.activeText};
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  text-align: left;
`

const StyledDropdownTrigger = styled(DropdownMenu.Trigger)`
  padding: 0.5rem 0.5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: none;
  border-radius: 0.375rem;
  background-color: ${({ theme }) => theme.forecast.defaultGhostButton};
  min-height: 32px;
  width: 100%;
  color: ${({ theme }) => theme.forecast.activeText};
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  cursor: pointer;
  text-align: left;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${({ theme }) => theme.forecast.hoverGhostButton};
  }

  &:active {
    background-color: ${({ theme }) => theme.forecast.activeGhostButton};
  }

  &:focus {
    outline: 2px solid ${({ theme }) => theme.forecast.border2};
    outline-offset: -2px;
  }

  &[data-state='open'] {
    background-color: ${({ theme }) => theme.forecast.hoverGhostButton};
  }
`

const LabelContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 0.5rem;
`

const LabelSpan = styled.span`
  flex: 1;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`

const CaretSpan = styled.span`
  flex-shrink: 0;
  color: ${({ theme }) => theme.forecast.tertiaryText};
  font-size: 12px;
  line-height: 1;
  opacity: 0.7;
`

const SkeletonContainer = styled.div`
  width: 100%;

  .ant-skeleton {
    width: 80%;
    height: 0.75rem;
    display: block;

    .ant-skeleton-input {
      min-width: 0;
      width: 100%;
      height: 100%;
    }
  }
`

const ButtonContent: React.FC<{
  contentData: GeckoOrganization | GeckoSite | undefined
  isSingleItem?: boolean
  isLoading?: boolean
}> = ({ contentData, isSingleItem, isLoading }) => {
  if (isLoading) {
    return (
      <SkeletonContainer>
        <Skeleton.Input active />
      </SkeletonContainer>
    )
  }

  return (
    <LabelContainer>
      <LabelSpan data-testid={`${contentData?.data.name}-content-dropdown`}>
        {contentData?.data.name}
      </LabelSpan>
      {!isSingleItem && <CaretSpan>▾</CaretSpan>}
    </LabelContainer>
  )
}

type WrapperComponentProps = {
  children: ReactNode
  disabled?: boolean
  'data-testid'?: string
}

export const DropdownSearchButton: React.FC<{
  contentData: GeckoOrganization | GeckoSite | undefined
  disabled?: boolean
  isSingleItem?: boolean
  isLoading?: boolean
  testId?: string
}> = ({ contentData, disabled, isSingleItem, isLoading, testId }) => {
  const { hasPermissions } = useGeckoPermissions()

  const renderDropdownTrigger = (
    WrapperComponent: React.ComponentType<WrapperComponentProps>
  ) => (
    <WrapperComponent disabled={disabled} data-testid={testId}>
      <ButtonContent
        contentData={contentData}
        isSingleItem={isSingleItem}
        isLoading={isLoading}
      />
    </WrapperComponent>
  )

  const isAdmin = hasPermissions([PERMISSION_CANTILEVER_CORE_ADMIN])
  const shouldBeNonInteractive =
    isSingleItem && contentData && !isLoading && !isAdmin

  return shouldBeNonInteractive
    ? renderDropdownTrigger(StyledContainer)
    : renderDropdownTrigger(StyledDropdownTrigger)
}
